import type { ReactNode } from 'react';
import { useAuth } from '../contexts/index';
import { ROLES } from '../core/constants/roles';

/**
 * AuthGuard — renders children only when a user is authenticated.
 * Renders `fallback` otherwise (login screen, redirect, etc.).
 */
export function AuthGuard({
  children,
  fallback = null,
}: {
  children: ReactNode;
  fallback?: ReactNode;
}) {
  const { user } = useAuth();
  return user ? children : fallback;
}

/**
 * RoleGuard — renders children only when the authenticated user has
 * one of the allowed roles.
 */
export function RoleGuard({
  children,
  allow,
  fallback = null,
}: {
  children: ReactNode;
  allow: string[];
  fallback?: ReactNode;
}) {
  const { user } = useAuth();
  if (!user) return fallback;
  if (!allow.includes(user.role)) return fallback;
  return children;
}

/** GodGuard — convenience guard for God-only features. */
export function GodGuard({
  children,
  fallback = null,
}: {
  children: ReactNode;
  fallback?: ReactNode;
}) {
  return <RoleGuard allow={[ROLES.GOD]} fallback={fallback}>{children}</RoleGuard>;
}

export { MaintenanceGuard } from './MaintenanceGuard';
